<template>
  <main class="training-detail-page">
    <div class="training-shell">
      <template v-if="session">
        <MarketingToolHeader
          eyebrow="Akademie Schulung"
          :title="session.title"
          :description="session.description || 'Alle Details, Voraussetzungen und deine Anmeldung auf einen Blick.'"
          icon="mdi-calendar-star-outline">
          <template v-slot:actions>
            <v-btn icon class="icon-action" aria-label="Zurück zum Schulungsplan" title="Zurück zum Schulungsplan" @click="$router.push('/akademie/schulungen')">
              <v-icon>mdi-arrow-left</v-icon>
            </v-btn>
            <span class="phase-badge" :class="phaseClass(getPhase(session))">{{ getPhase(session) }}</span>
            <span v-if="session.isRefresher" class="refresh-badge"><v-icon>mdi-refresh</v-icon>Auffrischung</span>
          </template>
        </MarketingToolHeader>

        <div v-if="session.thumbnailUrl" class="training-image" :style="{ backgroundImage: `url(${session.thumbnailUrl})` }" role="img" :aria-label="session.title">
          <div class="training-image-overlay"></div>
        </div>

        <div class="detail-grid">
          <div class="detail-content">
            <section class="info-grid" aria-label="Schulungsdetails">
              <v-card dark flat class="info-card">
                <span class="info-icon"><v-icon>mdi-calendar-clock-outline</v-icon></span>
                <div>
                  <small>Datum &amp; Zeit</small>
                  <strong>{{ formatDate(session.startsAt) }}</strong>
                  <span>{{ formatTime(session.startsAt) }} – {{ formatTime(session.endsAt) }}</span>
                </div>
              </v-card>
              <v-card dark flat class="info-card">
                <span class="info-icon icon-blue"><v-icon>mdi-map-marker-outline</v-icon></span>
                <div>
                  <small>Ort</small>
                  <strong>{{ session.location || "Ort noch offen" }}</strong>
                  <span>Details zur Durchfuehrung</span>
                </div>
              </v-card>
            </section>

            <v-card dark flat class="content-surface requirements-surface">
              <div class="surface-header">
                <span class="surface-icon"><v-icon>mdi-clipboard-check-outline</v-icon></span>
                <div>
                  <div class="surface-eyebrow">Vor der Anmeldung</div>
                  <h2>Voraussetzungen</h2>
                  <p>Diese Lernmodule muessen vor der Schulung abgeschlossen sein.</p>
                </div>
              </div>

              <div v-if="missingModules.length" class="requirement-state has-missing">
                <span><v-icon>mdi-alert-circle-outline</v-icon></span>
                <div>
                  <strong>Noch nicht alle Voraussetzungen erfüllt</strong>
                  <ul>
                    <li v-for="moduleItem in missingModules" :key="moduleItem._id">{{ moduleItem.title }}</li>
                  </ul>
                </div>
              </div>
              <div v-else class="requirement-state is-ready">
                <span><v-icon>mdi-check</v-icon></span>
                <div>
                  <strong>Alles bereit</strong>
                  <p>Du erfüllst alle Voraussetzungen für diesen Termin.</p>
                </div>
              </div>
            </v-card>
          </div>

          <aside>
            <v-card dark flat class="booking-card">
              <div class="booking-heading">
                <div>
                  <div class="surface-eyebrow">Dein Platz</div>
                  <h2>Anmeldung</h2>
                </div>
                <span class="booking-icon"><v-icon>mdi-ticket-confirmation-outline</v-icon></span>
              </div>

              <div class="seat-overview">
                <div>
                  <small>Verfügbare Plätze</small>
                  <strong>{{ availableSeats }} <span>/ {{ session.maxParticipants }}</span></strong>
                </div>
                <v-progress-circular :value="seatUsage" color="#009640" background-color="rgba(255,255,255,0.08)" size="64" width="6">
                  <v-icon color="#7ee5a6">mdi-account-multiple-outline</v-icon>
                </v-progress-circular>
              </div>

              <template v-if="session.myRegistration && session.myRegistration.status !== 'cancelled'">
                <div class="registration-state" :class="session.myRegistration.status === 'waiting_list' ? 'is-waiting' : 'is-confirmed'">
                  <span><v-icon>{{ session.myRegistration.status === "waiting_list" ? "mdi-clock-outline" : "mdi-check" }}</v-icon></span>
                  <div>
                    <strong>{{ statusLabel(session.myRegistration.status) }}</strong>
                    <p>Du bist für diesen Termin eingetragen.</p>
                  </div>
                </div>
                <v-btn block large outlined color="#ff6b78" class="cancel-action" :loading="cancelling" @click="cancelRegistration">
                  <v-icon left>mdi-close-circle-outline</v-icon>
                  Anmeldung stornieren
                </v-btn>
              </template>

              <template v-else>
                <v-tooltip bottom :disabled="canRegister">
                  <template v-slot:activator="{ on, attrs }">
                    <div v-bind="attrs" v-on="on">
                      <v-btn
                        block
                        x-large
                        depressed
                        :color="canRegister ? '#009640' : 'rgba(255,255,255,0.08)'"
                        class="register-action"
                        :disabled="!canRegister || registering"
                        :loading="registering"
                        @click="register">
                        <v-icon left v-if="canRegister">mdi-check-circle-outline</v-icon>
                        <span v-if="availableSeats === 0 && canRegister">Auf Warteliste setzen</span>
                        <span v-else>Jetzt anmelden</span>
                      </v-btn>
                    </div>
                  </template>
                  <span>Du musst zuerst die fehlenden Module abschliessen.</span>
                </v-tooltip>
              </template>

              <p class="cancellation-note">
                <v-icon>mdi-information-outline</v-icon>
                Kostenfreie Stornierung bis {{ session.cancellationDeadlineHours }}h vor Beginn möglich.
              </p>
            </v-card>
          </aside>
        </div>
      </template>

      <div v-else class="loading-surface">
        <v-progress-circular v-if="loading" indeterminate color="#58d388" size="48" width="4" />
        <template v-else>
          <span><v-icon>mdi-calendar-remove-outline</v-icon></span>
          <h2>Schulung nicht verfügbar</h2>
          <p>Der Termin konnte nicht geladen werden.</p>
          <v-btn outlined color="#58d388" class="back-action" to="/akademie/schulungen">Zum Schulungsplan</v-btn>
        </template>
      </div>

      <v-snackbar v-model="snackbar.show" :color="snackbar.color" :timeout="4000" top right elevation="24">
        <div class="snackbar-copy">
          <v-icon color="white">{{ snackbar.icon }}</v-icon>
          <span>{{ snackbar.text }}</span>
        </div>
        <template v-slot:action="{ attrs }">
          <v-btn icon v-bind="attrs" aria-label="Meldung schliessen" @click="snackbar.show = false"><v-icon>mdi-close</v-icon></v-btn>
        </template>
      </v-snackbar>
    </div>
  </main>
</template>

<script>
import api from "@/api"
import MarketingToolHeader from "@/views/dashboard/communication/MarketingToolHeader.vue"

export default {
  name: "SchulungDetail",
  components: { MarketingToolHeader },
  data() {
    return {
      session: null,
      missingModules: [],
      registering: false,
      cancelling: false,
      loading: false,
      snackbar: { show: false, color: "success", text: "", icon: "mdi-check-circle" },
    }
  },
  computed: {
    canRegister() {
      return this.missingModules.length === 0
    },
    availableSeats() {
      if (!this.session) return 0
      return Math.max(0, this.session.maxParticipants - (this.session.registeredCount || 0))
    },
    seatUsage() {
      if (!this.session || !this.session.maxParticipants) return 0
      return Math.min(100, ((this.session.registeredCount || 0) / this.session.maxParticipants) * 100)
    },
  },
  mounted() {
    this.loadSession()
  },
  methods: {
    async loadSession() {
      this.loading = true
      try {
        this.session = await api.akademie.fetchTermin(this.$route.params.id)
        if (this.session && this.session.requiredModules) {
          const progressEntries = await api.akademie.fetchProgress()
          this.missingModules = this.session.requiredModules.filter((requiredModule) => {
            const progress = progressEntries.find(
              (entry) => (entry.module && (entry.module._id || entry.module)) === requiredModule._id,
            )
            return !(progress && progress.completedAt)
          })
        }
      } catch (error) {
        console.error("Fehler beim Laden des Termins:", error)
        this.notify("error", "Termin konnte nicht geladen werden", "mdi-alert-circle")
      } finally {
        this.loading = false
      }
    },
    formatDate(date) {
      if (!date) return ""
      return new Date(date).toLocaleDateString("de-CH", { weekday: "long", year: "numeric", month: "long", day: "numeric" })
    },
    formatTime(date) {
      if (!date) return ""
      return new Date(date).toLocaleTimeString("de-CH", { hour: "2-digit", minute: "2-digit" }) + " Uhr"
    },
    getPhase(session) {
      if (!session.registrationStart || !session.registrationEnd) return "Unbekannt"
      const now = new Date()
      const start = new Date(session.registrationStart)
      const end = new Date(session.registrationEnd)
      if (now < start) return "Geplant"
      if (now > end) return "Geschlossen"
      return "Geoeffnet"
    },
    phaseClass(phase) {
      if (phase === "Geoeffnet") return "phase-open"
      if (phase === "Geplant") return "phase-planned"
      return "phase-closed"
    },
    statusLabel(status) {
      const labels = { registered: "Angemeldet", waiting_list: "Auf Warteliste", attended: "Teilgenommen" }
      return labels[status] || status
    },
    notify(color, text, icon = "mdi-information") {
      this.snackbar = { show: true, color, text, icon }
    },
    async register() {
      if (!this.canRegister || !this.session) return
      if (this.session.myRegistration && this.session.myRegistration.status !== "cancelled") {
        this.notify("warning", "Du bist bereits angemeldet.", "mdi-information")
        return
      }
      this.registering = true
      try {
        const registration = await api.akademie.registerForTermin(this.session._id)
        this.$set(this.session, "myRegistration", { _id: registration._id, status: registration.status })
        this.$set(
          this.session,
          "registeredCount",
          (this.session.registeredCount || 0) + (registration.status === "registered" ? 1 : 0),
        )
        if (registration.status === "waiting_list") {
          this.notify("warning", "Plätze ausgebucht – du stehst auf der Warteliste.", "mdi-clock-outline")
        } else {
          this.notify("success", "Anmeldung erfolgreich!", "mdi-check-circle")
        }
      } catch (error) {
        const message = (error.response && error.response.data && error.response.data.error) || "Anmeldung fehlgeschlagen"
        if (error.response && error.response.status === 409 && error.response.data && error.response.data.registration) {
          this.$set(this.session, "myRegistration", {
            _id: error.response.data.registration._id,
            status: error.response.data.registration.status,
          })
        }
        this.notify("error", message, "mdi-alert-circle")
      } finally {
        this.registering = false
      }
    },
    async cancelRegistration() {
      if (!this.session || !this.session.myRegistration) return
      if (!confirm("Anmeldung wirklich stornieren?")) return
      this.cancelling = true
      try {
        await api.akademie.cancelRegistration(this.session.myRegistration._id)
        this.$set(this.session, "myRegistration", null)
        if (this.session.registeredCount > 0) this.session.registeredCount -= 1
        this.notify("success", "Anmeldung storniert.", "mdi-check-circle")
      } catch (error) {
        this.notify("error", "Stornierung fehlgeschlagen.", "mdi-alert-circle")
      } finally {
        this.cancelling = false
      }
    },
  },
}
</script>

<style scoped>
.training-detail-page {
  --surface: rgba(255, 255, 255, 0.035);
  --surface-strong: rgba(255, 255, 255, 0.055);
  --border: rgba(255, 255, 255, 0.1);
  --border-strong: rgba(255, 255, 255, 0.16);
  --text: #f4f4f5;
  --muted: #a3a3a8;
  min-height: 100vh;
  min-height: 100dvh;
  padding: 1.5rem;
  color: var(--text);
  background: radial-gradient(circle at 15% -5%, rgba(0, 150, 64, 0.13), transparent 28rem), #121212;
  font-size: 1rem;
}

.training-shell {
  width: 100%;
  max-width: 78rem;
  margin: 0 auto;
}

.icon-action {
  width: 2.75rem !important;
  min-width: 2.75rem !important;
  height: 2.75rem !important;
  border: 1px solid var(--border);
  border-radius: 0.8125rem !important;
  color: #ffffff !important;
  background: rgba(255, 255, 255, 0.055) !important;
}

.phase-badge,
.refresh-badge {
  display: inline-flex;
  min-height: 2.75rem;
  align-items: center;
  gap: 0.4375rem;
  padding: 0.625rem 0.875rem;
  border-radius: 0.8125rem;
  font-size: 0.875rem;
  font-weight: 750;
}

.phase-open {
  color: #7ee5a6;
  background: rgba(0, 150, 64, 0.15);
}

.phase-planned {
  color: #fcd34d;
  background: rgba(245, 158, 11, 0.15);
}

.phase-closed,
.refresh-badge {
  color: #d4d4d8;
  background: rgba(255, 255, 255, 0.075);
}

.refresh-badge .v-icon {
  color: currentColor;
}

.training-image {
  position: relative;
  min-height: 15rem;
  margin-top: 1rem;
  overflow: hidden;
  border: 1px solid var(--border);
  border-radius: 1.375rem;
  background-position: center;
  background-size: cover;
}

.training-image-overlay {
  position: absolute;
  inset: 0;
  background: linear-gradient(to top, rgba(18, 18, 18, 0.72), transparent 65%);
}

.detail-grid {
  display: grid;
  grid-template-columns: minmax(0, 1.65fr) minmax(19rem, 0.75fr);
  align-items: start;
  gap: 1rem;
  margin-top: 1rem;
}

.info-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.75rem;
}

.info-card {
  display: flex;
  min-height: 7.5rem;
  align-items: center;
  gap: 0.875rem;
  padding: 1rem;
  border: 1px solid var(--border);
  border-radius: 1.125rem !important;
  background: var(--surface) !important;
}

.info-icon,
.surface-icon,
.booking-icon {
  display: grid;
  flex: 0 0 auto;
  place-items: center;
  color: #58d388;
  background: rgba(0, 150, 64, 0.12);
}

.info-icon {
  width: 2.875rem;
  height: 2.875rem;
  border-radius: 0.875rem;
}

.info-icon.icon-blue {
  color: #7dd3fc;
  background: rgba(14, 165, 233, 0.11);
}

.info-icon .v-icon,
.surface-icon .v-icon,
.booking-icon .v-icon {
  color: currentColor;
}

.info-card > div {
  display: grid;
  min-width: 0;
}

.info-card small {
  color: var(--muted);
  font-size: 0.75rem;
  font-weight: 700;
}

.info-card strong {
  margin-top: 0.1875rem;
  color: #ffffff;
  font-size: 0.9375rem;
  line-height: 1.4;
}

.info-card div > span {
  margin-top: 0.1875rem;
  color: #b8b8bc;
  font-size: 0.75rem;
}

.content-surface,
.booking-card {
  border: 1px solid var(--border);
  border-radius: 1.375rem !important;
  background: rgba(255, 255, 255, 0.03) !important;
  box-shadow: 0 1rem 2.75rem rgba(0, 0, 0, 0.12);
}

.content-surface {
  margin-top: 1rem;
  padding: 1.375rem;
}

.surface-header,
.booking-heading,
.seat-overview,
.registration-state,
.snackbar-copy {
  display: flex;
  align-items: center;
}

.surface-header {
  gap: 0.75rem;
}

.surface-icon,
.booking-icon {
  width: 2.625rem;
  height: 2.625rem;
  border-radius: 0.8125rem;
}

.surface-eyebrow {
  color: #58d388;
  font-size: 0.75rem;
  font-weight: 800;
  letter-spacing: 0.12em;
  text-transform: uppercase;
}

.surface-header h2,
.booking-heading h2 {
  margin: 0.1875rem 0 0;
  color: var(--text);
  font-size: 1.25rem;
  line-height: 1.3;
}

.surface-header p {
  margin: 0.25rem 0 0;
  color: var(--muted);
  font-size: 0.875rem;
  line-height: 1.5;
}

.requirement-state {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  margin-top: 1rem;
  padding: 0.875rem;
  border: 1px solid var(--border);
  border-radius: 0.875rem;
}

.requirement-state > span {
  display: grid;
  width: 2.25rem;
  height: 2.25rem;
  flex: 0 0 auto;
  place-items: center;
  border-radius: 0.6875rem;
}

.requirement-state > span .v-icon {
  color: currentColor;
  font-size: 1.25rem;
}

.has-missing {
  color: #ff9aa4;
  background: rgba(244, 63, 94, 0.075);
}

.has-missing > span {
  background: rgba(244, 63, 94, 0.13);
}

.is-ready {
  color: #7ee5a6;
  background: rgba(0, 150, 64, 0.075);
}

.is-ready > span {
  background: rgba(0, 150, 64, 0.13);
}

.requirement-state strong {
  color: #ffffff;
  font-size: 0.9375rem;
}

.requirement-state p,
.requirement-state ul {
  margin: 0.25rem 0 0;
  color: #c7c7cb;
  font-size: 0.875rem;
  line-height: 1.5;
}

.requirement-state ul {
  padding-left: 1.25rem;
}

.booking-card {
  position: sticky;
  top: 1rem;
  padding: 1.25rem;
}

.booking-heading,
.seat-overview {
  justify-content: space-between;
  gap: 0.75rem;
}

.seat-overview {
  margin: 1.25rem 0;
  padding: 1rem 0;
  border-top: 1px solid rgba(255, 255, 255, 0.075);
  border-bottom: 1px solid rgba(255, 255, 255, 0.075);
}

.seat-overview > div {
  display: grid;
}

.seat-overview small {
  color: var(--muted);
  font-size: 0.75rem;
}

.seat-overview strong {
  color: #ffffff;
  font-size: 2rem;
  line-height: 1.15;
}

.seat-overview strong span {
  color: #a3a3a8;
  font-size: 1rem;
}

.registration-state {
  align-items: flex-start;
  gap: 0.75rem;
  margin-bottom: 0.75rem;
  padding: 0.875rem;
  border-radius: 0.875rem;
}

.registration-state > span {
  display: grid;
  width: 2.25rem;
  height: 2.25rem;
  flex: 0 0 auto;
  place-items: center;
  border-radius: 0.6875rem;
  background: rgba(255, 255, 255, 0.08);
}

.registration-state > span .v-icon {
  color: currentColor;
  font-size: 1.25rem;
}

.registration-state strong {
  color: #ffffff;
  font-size: 0.9375rem;
}

.registration-state p {
  margin: 0.1875rem 0 0;
  color: currentColor;
  font-size: 0.875rem;
  opacity: 0.9;
}

.is-confirmed {
  color: #7ee5a6;
  background: rgba(0, 150, 64, 0.13);
}

.is-waiting {
  color: #fcd34d;
  background: rgba(245, 158, 11, 0.13);
}

.register-action,
.cancel-action,
.back-action {
  min-height: 3rem !important;
  border-radius: 0.8125rem !important;
  font-size: 0.875rem !important;
  font-weight: 750;
  letter-spacing: 0;
  text-transform: none;
}

.register-action {
  color: #ffffff !important;
}

.cancellation-note {
  display: flex;
  align-items: flex-start;
  gap: 0.4375rem;
  margin: 0.875rem 0 0;
  color: var(--muted);
  font-size: 0.75rem;
  line-height: 1.5;
}

.cancellation-note .v-icon {
  color: #8f9095;
  font-size: 1rem;
}

.loading-surface {
  display: flex;
  min-height: 70vh;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  color: var(--muted);
  text-align: center;
}

.loading-surface > span {
  display: grid;
  width: 3.5rem;
  height: 3.5rem;
  margin-bottom: 0.875rem;
  place-items: center;
  border-radius: 1rem;
  color: #58d388;
  background: rgba(0, 150, 64, 0.12);
}

.loading-surface > span .v-icon {
  color: currentColor;
}

.loading-surface h2 {
  margin: 0;
  color: var(--text);
  font-size: 1.25rem;
}

.loading-surface p {
  margin: 0.375rem 0 0;
  font-size: 0.875rem;
}

.back-action {
  margin-top: 1rem;
}

.snackbar-copy {
  gap: 0.5rem;
}

@media (max-width: 78.9375rem) {
  .training-detail-page {
    padding: 1.125rem;
  }
}

@media (max-width: 59.9375rem) {
  .detail-grid {
    grid-template-columns: minmax(0, 1fr);
  }

  .booking-card {
    position: static;
  }
}

@media (max-width: 37.4375rem) {
  .training-detail-page {
    padding: 0.75rem;
  }

  .training-image {
    min-height: 11rem;
    margin-top: 0.75rem;
    border-radius: 1.0625rem;
  }

  .detail-grid {
    margin-top: 0.75rem;
  }

  .info-grid {
    grid-template-columns: minmax(0, 1fr);
    gap: 0.5rem;
  }

  .info-card {
    min-height: 6.5rem;
  }

  .content-surface,
  .booking-card {
    padding: 0.875rem;
    border-radius: 1.0625rem !important;
  }
}
</style>
